import React from "react";
import { Link } from "react-router-dom";
import { Brain, LineSegments, Target, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const PILLARS = [
  {
    icon: Brain,
    label: "HRBP rigour",
    body: "Decades of inside-the-room business partnering across product, engineering and GTM leadership.",
  },
  {
    icon: LineSegments,
    label: "Classification engines",
    body: "Modern people-data models that segment talent, signals and risk — beyond the usual dashboards.",
  },
  {
    icon: Target,
    label: "Diagnostic first",
    body: "Every engagement starts with a sharp diagnosis. No templated playbooks, no policy theatre.",
  },
];

const PRINCIPLES = [
  ["01", "Data beats opinion.", "Every recommendation lands with numbers, not narrative."],
  ["02", "Diagnose before deliver.", "We earn the right to prescribe by listening first."],
  ["03", "Modular, not monolithic.", "Engage what you need. Scale up when ready."],
  ["04", "Boardroom & 1:1 ready.", "Our output holds in both rooms — or we rewrite it."],
  ["05", "Discreet partnership.", "Trusted by founders and CHROs for sensitive org design, M&A integration and leadership-team rewiring."],
];

function GraphicGrid() {
  // abstract cyan data-grid panel
  return (
    <div className="absolute inset-0 bg-[#07070A] overflow-hidden">
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,240,255,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,240,255,0.18) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
      {/* diagonal beam */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(400px 240px at 70% 10%, rgba(0,240,255,0.28), transparent 60%)",
        }}
      />
      {/* data cells */}
      <div className="absolute inset-0 grid grid-cols-6 grid-rows-8 gap-0.5 p-3">
        {Array.from({ length: 48 }).map((_, i) => {
          const hot = [3, 7, 10, 14, 19, 22, 27, 33, 38, 41, 44].includes(i);
          return (
            <div
              key={i}
              className={`${
                hot ? "bg-cy/80 [box-shadow:0_0_8px_rgba(0,240,255,0.55)]" : "bg-white/[0.04]"
              }`}
            />
          );
        })}
      </div>
      {/* scanline */}
      <div className="absolute inset-0 scanline" />
      {/* center reticle */}
      <div className="absolute top-6 left-6 font-mono text-[10px] tracking-[0.22em] text-cy">
        FIELD · 02
      </div>
      <div className="absolute top-6 right-6 font-mono text-[10px] tracking-[0.22em] text-cy blink">
        ● LIVE
      </div>
      <div className="absolute bottom-12 left-6 right-6">
        <div className="font-display text-2xl md:text-3xl font-black text-white leading-none tracking-tight">
          3.1<span className="text-cy">×</span>
        </div>
        <div className="font-mono text-[10px] text-zinc-400 tracking-[0.22em] mt-2">
          PSYCH-SAFETY LIFT
        </div>
      </div>
    </div>
  );
}

function GraphicTerminal() {
  // abstract cyan terminal/code panel
  const lines = [
    ["01", "diagnose.sprint", "init"],
    ["02", "listening.system", "on"],
    ["03", "1:1.cadence", "2w"],
    ["04", "growth.convos", "+41%"],
    ["05", "trust.pulse", "0.87"],
    ["06", "deliver.module", "ship"],
  ];
  return (
    <div className="absolute inset-0 bg-[#07070A] overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,240,255,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,240,255,0.12) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <div className="absolute inset-0 p-5 md:p-7 flex flex-col">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-1.5 h-1.5 bg-cy rotate-45" />
          <span className="font-mono text-[10px] tracking-[0.22em] text-cy">
            humint.shell //
          </span>
          <span className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 ml-auto">
            v1
          </span>
        </div>
        <ul className="font-mono text-[11px] md:text-xs space-y-1.5 flex-1">
          {lines.map(([n, k, v]) => (
            <li key={n} className="flex items-center gap-3">
              <span className="text-zinc-600">{n}</span>
              <span className="text-zinc-300">{k}</span>
              <span className="flex-1 border-b border-dashed border-ink-500" />
              <span className="text-cy">{v}</span>
            </li>
          ))}
        </ul>
        <div className="font-mono text-[11px] text-cy mt-3">
          &gt; <span className="blink">_</span>
        </div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#050505] text-white min-h-screen">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 md:pt-40 pb-16 grid-bg overflow-hidden">
          <div className="absolute inset-0 beam pointer-events-none" />
          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-6 rise d-1">
              / About
            </div>
            <h1
              data-testid="about-headline"
              className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-white max-w-4xl rise d-2"
            >
              Engineered for
              <span className="block text-cy">Bangalore&apos;s tech ecosystem.</span>
            </h1>
          </div>
        </section>

        {/* Brand Story */}
        <section className="bg-[#070709] border-y border-ink-600">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-5">
              <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-6">
                00 · The Practice
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-snug">
                HR advisory at the speed of tech.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <p
                data-testid="about-body"
                className="text-lg md:text-xl text-zinc-300 leading-relaxed"
              >
                <span className="font-display font-black text-white">HUMINT</span>
                <span className="text-cy">.</span> — short for{" "}
                <span className="italic text-zinc-500">Human Intelligence</span> — is an HR
                advisory built to operate at the speed of Bangalore&apos;s IT, Product and AI
                organisations. We combine{" "}
                <span className="text-white font-semibold">HRBP rigour</span> with{" "}
                <span className="text-white font-semibold">modern classification engines</span> to
                deliver diagnoses that hold up in a boardroom — and in a 1:1.
              </p>
              <div className="mt-10 grid sm:grid-cols-3 border-t border-l border-ink-500">
                {PILLARS.map((p, i) => (
                  <div
                    key={p.label}
                    data-testid={`about-pillar-${i}`}
                    className="group p-6 border-r border-b border-ink-500 bg-[#050505] hover:bg-[#0B0B10] transition-colors"
                  >
                    <p.icon size={24} weight="duotone" className="text-cy" />
                    <div className="mt-5 font-display text-base md:text-lg font-bold text-white tracking-tight">
                      {p.label}
                    </div>
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className="bg-[#050505]">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
              01 · Non-negotiables
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight mb-10 max-w-xl">
              Five non-negotiables.
            </h2>
            <div className="grid md:grid-cols-2 gap-0 border-t border-l border-ink-500">
              {PRINCIPLES.map(([n, t, body]) => (
                <div
                  key={n}
                  data-testid={`principle-${n}`}
                  className="group p-6 md:p-8 border-r border-b border-ink-500 bg-[#070709] hover:bg-[#0B0B10] transition-colors"
                >
                  <div className="font-mono text-xs text-cy mb-4">/ {n}</div>
                  <h3 className="font-display text-lg md:text-xl font-bold text-white tracking-tight">
                    {t}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Workplace imagery band */}
        <section
          data-testid="about-workplace-band"
          className="relative bg-[#050505] border-t border-ink-600"
        >
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
                  02 · On the floor
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight max-w-xl">
                  The rooms where the
                  <span className="block text-zinc-500 italic font-medium">
                    diagnostics actually happen.
                  </span>
                </h2>
              </div>
              <p className="max-w-md text-sm md:text-base text-zinc-300 leading-relaxed">
                <span className="text-white">Proof that people strategy drives business performance.</span>
                <br />
                <span className="text-zinc-400">
                  Real engagements. Real numbers. Redacted names — because the work speaks louder
                  than the logos.
                </span>
              </p>
            </div>

            <div className="grid grid-cols-12 gap-3 md:gap-4">
              {[
                {
                  type: "photo",
                  src: "https://images.unsplash.com/photo-1596176530529-78163a4f7af2?auto=format&fit=crop&w=1400&q=60",
                  label: "Founder Huddle · Whitefield",
                  span: "col-span-12 md:col-span-6 aspect-[4/3]",
                },
                {
                  type: "graphic",
                  variant: "grid",
                  label: "Deep-Work Block · Koramangala",
                  span: "col-span-6 md:col-span-3 aspect-[3/4]",
                },
                {
                  type: "photo",
                  src: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=60",
                  label: "Pulse Check · Indiranagar",
                  span: "col-span-6 md:col-span-3 aspect-[3/4]",
                },
                {
                  type: "graphic",
                  variant: "terminal",
                  label: "Cadence Loop · HSR Layout",
                  span: "col-span-12 md:col-span-4 aspect-[4/3]",
                },
                {
                  type: "photo",
                  src: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=60",
                  label: "Discover Sprint · ORR Tech Park",
                  span: "col-span-12 md:col-span-8 aspect-[16/7]",
                },
              ].map((img, i) => (
                <figure
                  key={i}
                  data-testid={`workplace-img-${i}`}
                  className={`group relative overflow-hidden border border-ink-500 bg-ink-700 ${img.span}`}
                >
                  {img.type === "photo" ? (
                    <>
                      <img
                        src={img.src}
                        alt={img.label}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 brightness-75 opacity-70 group-hover:opacity-85 transition-all duration-700 [filter:grayscale(1)_contrast(1.15)_blur(1px)]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/10" />
                      <div className="absolute inset-0 mix-blend-hue bg-cy" />
                      <div className="absolute inset-0 mix-blend-overlay bg-cy/15" />
                    </>
                  ) : img.variant === "grid" ? (
                    <GraphicGrid />
                  ) : (
                    <GraphicTerminal />
                  )}
                  <figcaption className="absolute bottom-3 left-3 right-3 flex items-center justify-between z-10">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cy">
                      / {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/90 bg-ink/70 backdrop-blur px-2 py-1">
                      {img.label}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* Founder / coverage */}
        <section className="bg-[#070709] border-y border-ink-600">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-6">
              <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
                03 · Coverage
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
                Rooted in Bangalore,
                <span className="block text-zinc-500 italic font-medium">open across India.</span>
              </h2>
              <p className="mt-6 text-zinc-400 max-w-lg">
                Our practice is calibrated to Bangalore&apos;s tech-talent dynamics, but the
                diagnostic model travels. We partner with multi-city organisations through a hybrid
                model — quarterly on-site rituals plus weekly async loops.
              </p>
            </div>

            <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-0 border-t border-l border-ink-500">
              {[
                ["15+", "Years in HR strategy"],
                ["40+", "Scale-ups advised"],
                ["12", "Bangalore sub-markets"],
                ["8", "Cities engaged nationally"],
              ].map(([k, l]) => (
                <div key={l} className="p-6 md:p-8 border-r border-b border-ink-500 bg-[#050505]">
                  <div className="font-display text-4xl md:text-5xl font-black text-white tracking-tight">
                    {k}
                  </div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-cy text-ink">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] mb-4">
              / Not sure where to start?
            </p>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-tight">
                We&apos;ll map it with you.
                <span className="block text-ink/70 italic font-medium text-2xl md:text-3xl mt-2">
                  Diagnose. Discover. Deliver.
                </span>
              </h2>
              <Link
                to="/contact"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 bg-ink text-cy px-8 py-4 text-sm font-semibold hover:bg-white hover:text-ink transition-colors"
              >
                Book a diagnostic call
                <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
